import React, { useEffect, useRef, useState } from 'react';
import useQueryGetNotificationBio, { IAlertBio } from '@/api/notification/getNotificationBio.ts';
import { format } from 'date-fns';
import useDebounce from '@/util/hook/useDebounce.tsx';
import Pagination from '@/component/pagination/Pagination.tsx';
import BioAlertModal from '@/pages/notification/bio/BioAlertModal.tsx';
import { useTranslation } from 'react-i18next';
import { equalString } from '@/util/strings.ts';

export interface IProps {
    className?: string;
}

const PAGE_SIZE = 10;

const NotificationBioContainer: React.FC<IProps> = ({ className }) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [search] = useState('');
    const [detailModalId, setDetailModalId] = useState<number>(-1);
    const detailModalRef = useRef<any>();
    const debouncedSearchText = useDebounce(search, 200);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchText]);

    const { data, refetch } = useQueryGetNotificationBio({
        pageNum: currentPage,
        pageSize: PAGE_SIZE,
    }, debouncedSearchText);

    useEffect(() => {
        refetch();
    }, [currentPage]);

    return (
        <div className={`p-4 pb-32 ${className}`}>
            <div className="grid gap-4">
                {Array.isArray(data?.data) && data?.data.flat().map((item: IAlertBio) => {
                    const { hrate, hrateStatus, resp, respStatus } = item?.alert?.nameDetail || {};

                    const heartRateElement = hrate !== undefined ? (
                        <span className={equalString(hrateStatus, '정상') ? 'text-main_b' : 'text-sub_r'}>
                            {t('notification.bio.table.state.heartRate')}[{hrate}]
                        </span>
                    ) : null;

                    const respElement = resp !== undefined ? (
                        <span className={resp >= 22 || !equalString(respStatus, '정상') ? 'text-sub_r' : 'text-main_b'}>
                            {t('notification.bio.table.state.respiration')}[{resp}]
                        </span>
                    ) : null;

                    return (
                        <div key={item.id} className="bg-gray-200 shadow-lg rounded-2xl p-6 border border-gray-300 cursor-pointer hover:shadow-xl transition-shadow flex flex-col" onClick={() => {
                            setDetailModalId(item.id);
                            detailModalRef.current?.show();
                        }}>
                            <div className="flex justify-between items-center mb-2">
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                </div>
                                <span className="text-gray-500 text-sm font-medium text-right">{t('notification.bio.title')}</span>
                            </div>
                            <hr className="border-gray-300 my-2" />
                            <div className="text-sm text-gray-600 flex justify-between py-2">
                                <span>{t('notification.alert.time')}</span>
                                <span className="font-semibold text-gray-900">{format(item?.alert?.createdAt, 'M월 dd일 오전 HH:mm')}</span>
                            </div>
                            <div className="text-sm text-gray-600 flex justify-between py-2">
                                <span>{t('notification.bio.table.state.column')}</span>
                                <span className="font-semibold text-gray-900">
                                    {heartRateElement}
                                    {heartRateElement && respElement ? ' / ' : ''}
                                    {respElement}
                                </span>
                            </div>
                            <div className="text-sm text-gray-600 flex justify-between py-2">
                                <span>{t('notification.alert.address')}</span>
                                <span className="font-semibold text-gray-900 text-right">{item?.alert?.location?.address}</span>
                            </div>
                            <hr className="border-gray-300 my-2" />
                            <div className="text-center text-gray-500 text-sm cursor-pointer py-2">{t('notification.table.viewDetail')}</div>
                        </div>
                    );
                })}
            </div>
            {(data?.pagination?.totalPages || 0) > 0 && (
                <Pagination
                    className="mt-4"
                    current_page={data?.pagination?.currentPage}
                    page_size={PAGE_SIZE}
                    total_page={data?.pagination?.totalPages}
                    fetchPage={(pageIndex) => {
                        setCurrentPage(pageIndex);
                    }}
                />
            )}
            <BioAlertModal ref={detailModalRef} defaultId={detailModalId} refetchAlerts={refetch} />
        </div>
    );
};

export default NotificationBioContainer;
